import React from "react";
import { Link } from "react-router-dom";
import { useConfig } from "../context/ConfigContext";
import { useI18n } from "../context/I18nContext";
import {
  Mail, Phone, MapPin, Clock,
  Mail as MailIcon, Phone as PhoneIcon, Clock as ClockIcon, MapPin as MapPinIcon,
  Shield, Sparkles, ArrowUpRight,
} from "lucide-react";
import useSiteContent, { findBlock } from "../hooks/useSiteContent";
import { sanitizeNavUrl, isInternalUrl } from "../lib/urlSafe";
import { useBranding } from "../context/BrandingContext";

/* ----------------------------------------------------------------------
 * Footer — Release 1.C
 * Premium #082c50 deep blue with refined 4-column grid, premium typography,
 * trust signals and subtle gradient mesh. CMS overlay drives the columns;
 * legacy footer remains a hard fallback so links never disappear.
 * ---------------------------------------------------------------------- */

const DESTINATIONS = [
  { label: "Curaçao", to: "/shipping-to-curacao" },
  { label: "Bonaire", to: "/hubs#bonaire" },
  { label: "Aruba", to: "/hubs#aruba" },
  { label: "Sint Maarten", to: "/hubs#sint-maarten" },
  { label: "Suriname", to: "/hubs#suriname" },
  { label: "Miami", to: "/hubs#miami" },
  { label: "Venezuela", to: "/hubs#venezuela" },
  { label: "Colombia", to: "/hubs#colombia" },
  { label: "Dominicaanse Republiek", to: "/hubs#dominican-republic" },
  { label: "Jamaica", to: "/hubs#jamaica" },
  { label: "Guyana", to: "/hubs#guyana" },
];

function _SafeLink({ href, children, className }) {
  const safe = sanitizeNavUrl(href);
  if (!safe) return null;
  if (isInternalUrl(safe)) {
    return <Link to={safe} className={className}>{children}</Link>;
  }
  return (
    <a href={safe} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}

function PremiumShell({ children, legalText }) {
  const year = new Date().getFullYear();
  // CMS-driven legal text. Three states matter:
  //   - undefined  → use hardcoded fallback (legacy + first-render only)
  //   - ""         → admin explicitly cleared it; show nothing on that side
  //   - "..."      → render exactly what the admin published, byte-for-byte
  const hasCmsLegal = legalText !== undefined && legalText !== null;
  const renderedLegal = hasCmsLegal
    ? legalText
    : `© ${year} 5999Cargo Group Europe. Alle rechten voorbehouden.`;
  return (
    <footer data-testid="site-footer" className="relative hero-mesh-dark text-white overflow-hidden">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 grid-pattern-light opacity-50 pointer-events-none" aria-hidden="true" />

      <div className="relative container-x py-20">
        {children}
      </div>

      {/* Phase B-Polish — vaste Klantcentrum-balk (los van CMS-kolommen) */}
      <div className="relative border-t border-white/10 bg-black/20">
        <div className="container-x py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <span className="text-white/60 font-chivo tracking-wider uppercase text-[11px]">Klantcentrum</span>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link
              to="/shop-account/login"
              className="inline-flex items-center gap-1.5 text-white/85 hover:text-white font-chivo font-bold tracking-wider text-[12px] uppercase transition-colors"
              data-testid="footer-shop-account"
            >
              Mijn 5999Cargo
              <ArrowUpRight className="w-3 h-3" />
            </Link>
            <span className="text-white/30">·</span>
            <Link
              to="/mijn-5999cargo/zendingen"
              className="inline-flex items-center gap-1.5 text-white/85 hover:text-white font-chivo font-bold tracking-wider text-[12px] uppercase transition-colors"
              data-testid="footer-my5999cargo-permanent"
            >
              Mijn zendingen
              <ArrowUpRight className="w-3 h-3" />
            </Link>
            <span className="text-white/30 hidden sm:inline">·</span>
            <span className="text-white/50 text-[11px] hidden sm:inline">
              Bekijk uw webshopbestellingen &amp; bewaar uw gegevens
            </span>
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="container-x py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/60">
          {renderedLegal ? (
            <span data-testid="footer-legal-text">{renderedLegal}</span>
          ) : (
            <span aria-hidden="true" />
          )}
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1.5 font-chivo tracking-[0.18em] uppercase text-white/70">
              <Sparkles className="w-3 h-3 text-brand-300" strokeWidth={2} />
              Rotterdam → Caribbean
            </span>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-white/60">
              <Shield className="w-3 h-3 text-brand-300" strokeWidth={2} />
              Secured & Insured
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function ColTitle({ children }) {
  return (
    <h4 className="text-[11px] tracking-[0.22em] uppercase text-brand-300 font-chivo font-bold mb-4">
      {children}
    </h4>
  );
}

function LinkItem({ to, children, testid, external }) {
  const cls = "inline-flex items-center gap-1 text-[14px] text-white/80 hover:text-white transition-colors group";
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid}>
        {children}
        <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </a>
    );
  }
  return (
    <Link to={to} className={cls} data-testid={testid}>
      {children}
    </Link>
  );
}

function BrandIntro({ tagline }) {
  const { footerLogoUrl, footerLogoAlt, hasCustomFooterLogo, hasCustomLogo } = useBranding();
  // Phase 18.15.4 — footer-specific logo wins; if admin only set the
  // header logo we still skip the invert filter because that logo was
  // designed for a light background; admin can upload a dark-bg
  // variant separately via Brandingbeheer → Footer logo.
  const useDarkBgVariant = hasCustomFooterLogo || hasCustomLogo;
  const imgClass = useDarkBgVariant
    ? "h-12 w-auto object-contain"
    : "h-12 w-auto object-contain brightness-0 invert";
  return (
    <div className="mb-14 max-w-2xl">
      <div className="inline-flex items-center gap-2 mb-5">
        <img
          src={footerLogoUrl}
          alt={footerLogoAlt}
          className={imgClass}
          data-testid="footer-logo"
        />
      </div>
      {tagline && (
        <p className="text-sm text-white/75 leading-relaxed max-w-xl" data-testid="footer-short-text">
          {tagline}
        </p>
      )}
    </div>
  );
}

function FooterLegacy() {
  const { email, phone, phone_display, address, whatsapp } = useConfig();
  const { t } = useI18n();

  const services = [
    { label: t("services.air_freight"), to: "/luchtvracht" },
    { label: t("services.sea_freight"), to: "/zeevracht" },
    { label: t("services.shop_drop_ship"), to: "/shop-drop-ship" },
    { label: "Pickup & Delivery", to: "/pickup-delivery" },
    { label: t("services.moving"), to: "/verhuizing" },
    { label: "Zakelijke zendingen", to: "/zakelijk" },
    { label: t("nav.pay_online"), to: "/online-betalen" },
    { label: "Mijn 5999Cargo", to: "/mijn-5999cargo" },
  ];

  return (
    <PremiumShell legalText={t("footer.rights")}>
      <BrandIntro tagline={t("footer.tagline")} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div data-testid="footer-services-col">
          <ColTitle>{t("footer_legal.services_title")}</ColTitle>
          <ul className="space-y-2.5">
            {services.map((s) => (
              <li key={s.label}><LinkItem to={s.to}>{s.label}</LinkItem></li>
            ))}
          </ul>
        </div>

        <div data-testid="footer-destinations-col">
          <ColTitle>{t("footer_legal.destinations_title")}</ColTitle>
          <ul className="space-y-2.5">
            {DESTINATIONS.map((d) => (
              <li key={d.label}><LinkItem to={d.to}>{d.label}</LinkItem></li>
            ))}
          </ul>
          <div className="mt-5">
            <Link
              to="/hubs"
              className="inline-flex items-center gap-1 text-xs font-chivo font-bold tracking-[0.18em] uppercase text-brand-300 hover:text-white transition-colors"
              data-testid="footer-all-hubs"
            >
              {t("nav.hubs")} <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </div>

        <div data-testid="footer-contact-col">
          <ColTitle>{t("footer_legal.contact_title")}</ColTitle>
          <ul className="space-y-3 text-sm">
            <li className="font-chivo font-bold text-white">
              5999Cargo Group Europe
            </li>
            <li className="flex items-start gap-2.5 text-white/80">
              <MapPin className="w-4 h-4 mt-0.5 text-brand-300 flex-shrink-0" strokeWidth={1.75} />
              <span data-testid="footer-address">{address}</span>
            </li>
            <li className="flex items-center gap-2.5 text-white/80">
              <Phone className="w-4 h-4 text-brand-300 flex-shrink-0" strokeWidth={1.75} />
              <a href={`tel:${phone}`} className="hover:text-white" data-testid="footer-phone">
                {phone_display || phone}
              </a>
            </li>
            <li className="flex items-center gap-2.5 text-white/80">
              <Mail className="w-4 h-4 text-brand-300 flex-shrink-0" strokeWidth={1.75} />
              <a href={`mailto:${email}`} className="hover:text-white break-all" data-testid="footer-email">
                {email}
              </a>
            </li>
            <li>
              <a
                data-testid="footer-whatsapp"
                href={`https://wa.me/${whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-lg bg-[#25D366] hover:bg-[#1fb858] text-white font-chivo font-bold text-xs tracking-wide uppercase shadow-brand-soft transition-colors"
              >
                {t("footer.whatsapp_us")}
              </a>
            </li>
          </ul>
        </div>

        <div data-testid="footer-legal-col">
          <ColTitle>{t("footer_legal.title")}</ColTitle>
          <p
            className="text-xs text-white/70 leading-relaxed"
            data-testid="footer-legal-body"
          >
            {t("footer_legal.body")}
          </p>
          <ul className="mt-5 space-y-2.5">
            <li><LinkItem to="/about">{t("nav.about")}</LinkItem></li>
            <li><LinkItem to="/blog">{t("nav.blog")}</LinkItem></li>
            <li><LinkItem to="/contact">{t("nav.contact")}</LinkItem></li>
            <li>
              <LinkItem to="/shop-account/login" testid="footer-account-login">Mijn 5999Cargo</LinkItem>
              <p className="text-[10px] text-white/40 leading-tight mt-0.5">Bekijk uw webshopbestellingen &amp; bewaar uw gegevens</p>
            </li>
            <li>
              <LinkItem to="/mijn-5999cargo" testid="footer-my5999cargo">Mijn 5999Cargo</LinkItem>
              <p className="text-[10px] text-white/40 leading-tight mt-0.5">Cargo-casillero &amp; zendingen volgen</p>
            </li>
          </ul>
        </div>
      </div>
    </PremiumShell>
  );
}

function FooterCms({ data }) {
  if (!data) return null;
  const cols = Array.isArray(data.columns) ? data.columns.slice(0, 6) : [];
  // Pick legal text from the canonical `legal_text` field with a
  // backwards-compat search through historical names. `undefined` →
  // PremiumShell shows the hardcoded fallback; `""` is respected as
  // an intentional admin choice to render nothing.
  const legalText =
    data.legal_text !== undefined ? data.legal_text :
    data.legalText !== undefined ? data.legalText :
    data.copyright !== undefined ? data.copyright :
    data.bottom_text !== undefined ? data.bottom_text :
    undefined;

  return (
    <PremiumShell legalText={legalText}>
      <BrandIntro tagline={data.tagline} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {cols.map((c, i) => (
          <div key={i} data-testid={`footer-col-${i}`}>
            {c.title && <ColTitle>{c.title}</ColTitle>}

            {Array.isArray(c.links) && (
              <ul className="space-y-2.5">
                {c.links.map((l, j) => {
                  const safe = sanitizeNavUrl(l.url);
                  if (!safe) return null;
                  return (
                    <li key={j}>
                      <_SafeLink
                        href={l.url}
                        className="inline-flex items-center gap-1 text-[14px] text-white/80 hover:text-white transition-colors"
                      >
                        {l.label}
                      </_SafeLink>
                    </li>
                  );
                })}
              </ul>
            )}

            {(c.phone || c.email || c.address || c.opening_hours || c.email_finance) && (
              <ul className="space-y-3 text-sm mt-3">
                {c.phone && (
                  <li className="flex items-center gap-2.5 text-white/80">
                    <PhoneIcon className="w-4 h-4 text-brand-300 flex-shrink-0" strokeWidth={1.75} />
                    <_SafeLink href={`tel:${c.phone.replace(/\s+/g,"")}`} className="hover:text-white">
                      {c.phone}
                    </_SafeLink>
                  </li>
                )}
                {c.email && (
                  <li className="flex items-center gap-2.5 text-white/80">
                    <MailIcon className="w-4 h-4 text-brand-300 flex-shrink-0" strokeWidth={1.75} />
                    <_SafeLink href={`mailto:${c.email}`} className="hover:text-white break-all">
                      {c.email}
                    </_SafeLink>
                  </li>
                )}
                {c.email_finance && (
                  <li className="flex items-center gap-2.5 text-white/80" data-testid="footer-email-finance">
                    <MailIcon className="w-4 h-4 text-brand-300 flex-shrink-0" strokeWidth={1.75} />
                    <_SafeLink href={`mailto:${c.email_finance}`} className="hover:text-white break-all">
                      {c.email_finance}
                    </_SafeLink>
                  </li>
                )}
                {c.address && (
                  <li className="flex items-start gap-2.5 text-white/80">
                    <MapPinIcon className="w-4 h-4 text-brand-300 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                    <span>{c.address}</span>
                  </li>
                )}
                {c.opening_hours && (
                  <li className="flex items-center gap-2.5 text-white/80">
                    <ClockIcon className="w-4 h-4 text-brand-300 flex-shrink-0" strokeWidth={1.75} />
                    <span>{c.opening_hours}</span>
                  </li>
                )}
              </ul>
            )}
          </div>
        ))}
      </div>
    </PremiumShell>
  );
}

export default function Footer() {
  const { blocks, loading } = useSiteContent("footer");
  if (loading) return <FooterLegacy />;
  const block = findBlock(blocks, "footer_columns");
  if (!block || !block.data) return <FooterLegacy />;
  return <FooterCms data={block.data} />;
}
